/**
 * Tutor modes and the live session meter.
 *
 * Owner (DEV 2026-09-24): two modes the learner can talk or type in - Voice (the tutor speaks) and
 * Chat (the tutor writes; "Text" until owner 2026-09-29) - items 5 and 9; talk mode sat on "Connecting..." (item 8); an exhausted
 * allowance must stop the tutor mid-way with a clear message (item 11). Sol owner14 Q2 and
 * owner14-e (A): the worker extends the session and sends what is left on topic quiklearn-meter.
 */
export const METER_TOPIC = 'quiklearn-meter';

/** "Chat": type or speak, the tutor replies in writing. The backend bills it to the Chat (text) meter. */
export const TEXT_MODE = 'HYBRID';

export const writesReplies = mode => mode === 'TEXT' || mode === 'HYBRID';
export const listensToLearner = mode => mode === 'VOICE' || mode === 'HYBRID';

/**
 * The mode's name as the Voice / Chat picker words it, for the indicator shown while a session runs
 * (Sol ui-leftovers G9: "Show a clear Written/Voice mode indicator and display microphone guidance only in Voice mode").
 */
export const tutorModeLabel = mode => (writesReplies(mode) ? 'Chat' : 'Voice');

/**
 * Whether the bar shows microphone guidance (the "microphone is off / allow microphone access" tab): Voice only.
 * Chat is typed first and works without a microphone, so it never shows it (Sol ui-leftovers G9).
 */
export const showsMicGuidance = ({active = false, mode, micEnabled = false, denied = false} = {}) =>
  Boolean(active && mode === 'VOICE' && !micEnabled && denied);

const decode = payload => {
  if (typeof payload === 'string') return payload;
  try {
    return new TextDecoder().decode(payload);
  } catch (_) {
    return '';
  }
};

/** {type: 'meter', secondsLeft} or {type: 'ended', ...}; null for anything else. */
export const parseMeterMessage = payload => {
  let data;
  try {
    data = JSON.parse(decode(payload));
  } catch (_) {
    return null;
  }
  if (data?.type === 'meter' && Number.isFinite(data.secondsLeft)) return data;
  if (data?.type === 'ended') return data;
  return null;
};

/** The questions offered on every tutor turn: generic, so no per-lesson content review (Sol onboarding-leftovers S1 B). */
export const TUTOR_SUGGESTIONS = ['Explain that more simply', 'Give me a real-world example', 'Ask me a quick question'];

/** "2 min left · this question is free" - only when the server granted the free tour question (Sol onboarding-build-2 D7). */
export const freeTourLabel = (label, free) => {
  if (!free) return label;
  return label ? `${label} · this question is free` : 'This question is free';
};

export const minutesLeftLabel = seconds => {
  if (!Number.isFinite(seconds)) return null;
  if (seconds < 60) return 'Less than a minute left';
  return `${Math.floor(seconds / 60)} min left`;
};

/**
 * What a session end opens (owner 2026-09-29): a used-up meter opens the full-screen plans, with the
 * other mode's minutes so they can offer "Use Chat instead"; a session that simply ended is not a
 * limit and shows only a quiet "Session ended · Start again" line.
 */
export const sessionEndNotice = ended => {
  const reason = ended?.reason || 'SESSION_ENDED';
  const meter = ended?.meter === 'voice' ? 'voice' : 'text';
  if (reason === 'QUOTA_EXHAUSTED' || reason === 'NO_SUBSCRIPTION') {
    const other = Number(ended?.otherSecondsLeft);
    return {
      kind: 'quota',
      meter,
      otherMeter: ended?.otherMeter === 'voice' ? 'voice' : ended?.otherMeter ? 'text' : null,
      otherSecondsLeft: Number.isFinite(other) ? other : 0,
      resetAt: ended?.resetAt || null,
    };
  }
  // the tutor closed after a quiet minute, to save the learner's minutes (Sol tutor-idle C)
  if (reason === 'IDLE') return {kind: 'idle', meter};
  return {kind: 'ended', meter};
};
